const formatMoney = (cents) => {
  const value = Math.round(cents) / 100;
  const hasCents = Math.round(cents) % 100 !== 0;
  return `$${value.toFixed(hasCents ? 2 : 0)}`;
};

class ProductCard extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const dataEl = this.querySelector("[data-card-data]");
    this.panel = this.querySelector("[data-panel]");
    this.toggle = this.querySelector("[data-panel-toggle]");
    this.closeBtn = this.querySelector("[data-panel-close]");
    this.pills = Array.from(this.querySelectorAll("[data-variant-pill]"));
    this.purchaseInputs = Array.from(this.querySelectorAll("[data-purchase]"));
    this.planSelect = this.querySelector("[data-plan-select]");
    this.frequency = this.querySelector("[data-frequency]");
    this.atc = this.querySelector("[data-atc]");
    this.error = this.querySelector("[data-error]");
    this.unitPrice = this.querySelector("[data-unit-price]");
    this.onetimePrice = this.querySelector("[data-onetime-price]");
    this.subscribePrice = this.querySelector("[data-subscribe-price]");
    this.saveLabel = this.querySelector("[data-save-label]");
    this.atcPrice = this.querySelector("[data-atc-price]");
    this.atcLabel = this.querySelector("[data-atc-label]");

    this._onDocumentClick = this._onDocumentClick.bind(this);

    if (this.toggle) {
      this.toggle.addEventListener("click", () => this.openPanel());
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => this.closePanel());
    }

    if (!dataEl) return;

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    this.variants = this.data.variants || [];
    const firstAvailable =
      this.variants.find((v) => v.available) || this.variants[0];
    const preferredId = Number(this.dataset.defaultVariant);
    const preferred = this.variants.find((v) => v.id === preferredId && v.available);
    this.variantId = preferred ? preferred.id : firstAvailable ? firstAvailable.id : null;
    this.purchase = this.hasAttribute("data-default-subscribe") ? "subscribe" : "onetime";
    this.planId = null;

    this.pills.forEach((pill) => {
      pill.addEventListener("click", () => {
        this.variantId = Number(pill.dataset.variantId);
        this.render();
      });
    });

    this.purchaseInputs.forEach((input) => {
      input.addEventListener("change", () => {
        this.purchase = input.value;
        this.render();
      });
    });

    if (this.planSelect) {
      this.planSelect.addEventListener("change", () => {
        this.planId = Number(this.planSelect.value);
        this.render();
      });
    }

    if (this.atc) {
      this.atc.addEventListener("click", () => this.addToCart());
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._onDocumentClick);
  }

  get variant() {
    return this.variants.find((v) => v.id === this.variantId) || null;
  }

  get plan() {
    const variant = this.variant;
    if (!variant || !variant.plans || !variant.plans.length) return null;
    return (
      variant.plans.find((p) => p.id === this.planId) ||
      variant.plans.find((p) => /4 weeks|1 month|every month/i.test(p.name)) ||
      variant.plans[0]
    );
  }

  openPanel() {
    this.classList.add("is-open");
    if (this.toggle) this.toggle.setAttribute("aria-expanded", "true");
    setTimeout(() => document.addEventListener("click", this._onDocumentClick), 0);
  }

  closePanel() {
    this.classList.remove("is-open");
    if (this.toggle) this.toggle.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", this._onDocumentClick);
  }

  _onDocumentClick(e) {
    if (!this.contains(e.target)) this.closePanel();
  }

  render() {
    const variant = this.variant;
    if (!variant) return;

    this.pills.forEach((pill) => {
      pill.setAttribute(
        "aria-pressed",
        Number(pill.dataset.variantId) === variant.id ? "true" : "false",
      );
    });

    if (this.unitPrice) {
      if (this.data.unitLabel && variant.units > 0) {
        this.unitPrice.textContent = `${formatMoney(variant.price / variant.units)} ${this.data.unitLabel}`;
      } else {
        this.unitPrice.textContent = formatMoney(variant.price);
      }
    }

    const hasPlans = variant.plans && variant.plans.length > 0;
    const plan = hasPlans ? this.plan : null;
    if (plan) this.planId = plan.id;

    if (this.planSelect && hasPlans) {
      const current = String(this.planId);
      const optionsHtml = variant.plans
        .map(
          (p) =>
            `<option value="${p.id}"${String(p.id) === current ? " selected" : ""}>${p.name}</option>`,
        )
        .join("");
      if (this.planSelect.innerHTML !== optionsHtml) {
        this.planSelect.innerHTML = optionsHtml;
      }
    }

    if (this.onetimePrice) this.onetimePrice.textContent = formatMoney(variant.price);
    if (this.subscribePrice && plan) this.subscribePrice.textContent = formatMoney(plan.price);
    if (this.saveLabel && plan && variant.price > 0) {
      const pct = Math.round(((variant.price - plan.price) / variant.price) * 100);
      this.saveLabel.textContent = pct > 0 ? `& Save ${pct}%` : "";
    }

    const subscribeInput = this.purchaseInputs.find((i) => i.value === "subscribe");
    if (!hasPlans && this.purchase === "subscribe") {
      this.purchase = "onetime";
      const onetime = this.purchaseInputs.find((i) => i.value === "onetime");
      if (onetime) onetime.checked = true;
    }
    if (subscribeInput) subscribeInput.disabled = !hasPlans;

    if (this.frequency) {
      this.frequency.classList.toggle("hidden", !(this.purchase === "subscribe" && hasPlans));
    }

    const activePrice = this.purchase === "subscribe" && plan ? plan.price : variant.price;
    if (this.atcPrice) this.atcPrice.textContent = formatMoney(activePrice);

    if (this.atc) {
      this.atc.disabled = !variant.available;
      this.atc.classList.toggle("opacity-60", !variant.available);
      if (this.atcLabel) {
        this.atcLabel.textContent = variant.available ? "Add to cart" : "Sold out";
      }
    }
  }

  async addToCart() {
    const variant = this.variant;
    const cart = document.querySelector("site-cart");
    if (!variant || !variant.available || !cart || this.adding) return;

    const sellingPlan =
      this.purchase === "subscribe" && this.plan ? this.plan.id : null;

    this.adding = true;
    if (this.error) this.error.classList.add("hidden");
    if (this.atcLabel) this.atcLabel.textContent = "Adding";

    try {
      await cart.addVariantToCart(variant.id, 1, sellingPlan);
      this.closePanel();
    } catch (e) {
      if (this.error) {
        this.error.textContent = "Something went wrong. Please try again.";
        this.error.classList.remove("hidden");
      }
    } finally {
      this.adding = false;
      this.render();
    }
  }
}

customElements.define("product-card", ProductCard);

class CarouselTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll("[data-tab-target]"));
    this.panels = Array.from(this.querySelectorAll("[data-tab-panel]"));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.activate(tab.dataset.tabTarget));
      tab.addEventListener("keydown", (e) => {
        const index = this.tabs.indexOf(tab);
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.preventDefault();
          const next =
            e.key === "ArrowRight"
              ? (index + 1) % this.tabs.length
              : (index - 1 + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.activate(this.tabs[next].dataset.tabTarget);
        }
      });
    });

    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (this.panels.length > 1 && "IntersectionObserver" in window) {
      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.preloadObserver.disconnect();
          this.preloadPanels();
        },
        { rootMargin: "800px 0px" },
      );
      this.preloadObserver.observe(this);
    }
  }

  disconnectedCallback() {
    if (this.preloadObserver) this.preloadObserver.disconnect();
  }

  preloadPanels() {
    this.panels.forEach((panel) => {
      if (!panel.classList.contains("hidden")) return;
      Array.from(panel.querySelectorAll(".product-card__image"))
        .slice(0, 5)
        .forEach((img) => {
          img.loading = "eager";
          if (typeof img.decode === "function") img.decode().catch(() => {});
        });
    });
  }

  staggerIn(panel) {
    if (this.reducedMotion) return;
    const bounds = panel.getBoundingClientRect();
    const slides = Array.from(panel.querySelectorAll(".swiper-slide"))
      .map((slide) => ({ slide, rect: slide.getBoundingClientRect() }))
      .filter(({ rect }) => rect.width > 0 && rect.right > bounds.left && rect.left < window.innerWidth)
      .sort((a, b) => a.rect.left - b.rect.left)
      .map(({ slide }) => slide);

    slides.forEach((slide, index) => {
      const image = slide.querySelector(".product-card__image");
      if (!image) return;
      image.style.animation = "";
      if (index === 0) return;
      image.style.animation = "none";
      void image.offsetWidth;
      image.style.animation = `tab-card-in 320ms ease ${index * 40}ms both`;
      image.addEventListener(
        "animationend",
        () => {
          image.style.animation = "";
        },
        { once: true },
      );
    });
  }

  activate(id) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.tabTarget === id;
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.setAttribute("tabindex", active ? "0" : "-1");
      tab.classList.toggle("is-active", active);
    });
    this.panels.forEach((panel) => {
      const active = panel.dataset.tabPanel === id;
      panel.classList.toggle("hidden", !active);
      if (active) {
        const carousel = panel.querySelector("site-carousel");
        requestAnimationFrame(() => {
          if (carousel && carousel.swiper) {
            carousel.swiper.update();
            carousel.swiper.slideTo(0, 0);
          }
          this.staggerIn(panel);
        });
      }
    });
  }
}

customElements.define("carousel-tabs", CarouselTabs);

class CategoryHover extends HTMLElement {
  connectedCallback() {
    this.images = Array.from(this.querySelectorAll("[data-category-image]"));
    const canHover = window.matchMedia("(hover: hover) and (min-width: 1024px)").matches;
    if (!this.images.length || !canHover) return;

    if (!("IntersectionObserver" in window)) {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.load();
    }, { rootMargin: "600px 0px" });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  load() {
    this.images.forEach((img) => {
      if (img.dataset.src && !img.getAttribute("src")) {
        img.setAttribute("src", img.dataset.src);
        if (img.decode) img.decode().catch(() => {});
      }
    });
  }
}

customElements.define("category-hover", CategoryHover);

class MarqueeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("button");
    this.marquee = this.closest("[data-section-id]")?.querySelector("smart-marquee");
    if (!this.button || !this.marquee) return;
    this.button.addEventListener("click", () => {
      const paused = this.marquee.classList.contains("is-paused");
      if (paused) {
        this.marquee.resume();
        this.marquee.removeAttribute("data-user-paused");
      } else {
        this.marquee.pause();
        this.marquee.setAttribute("data-user-paused", "");
      }
      this.button.setAttribute("aria-pressed", paused ? "false" : "true");
      this.button.setAttribute("aria-label", paused ? "Pause" : "Play");
      this.classList.toggle("is-paused", !paused);
    });
  }
}

customElements.define("marquee-toggle", MarqueeToggle);

class TestimonialMarquee extends HTMLElement {
  connectedCallback() {
    this.marquee = this.querySelector("smart-marquee");
    this.modal = this.closest("section")?.querySelector("[data-review-modal]");
    const cards = Array.from(this.querySelectorAll("[data-video-card][data-video-src]"));
    this.reviews = [];
    cards.forEach((card) => {
      const index = Number(card.dataset.reviewIndex);
      if (!this.reviews[index]) this.reviews[index] = { ...card.dataset };
      card.addEventListener("click", (e) => {
        if (e.target.closest(".testimonial-chip")) return;
        this.open(index);
      });
    });
    this.reviews = this.reviews.filter(Boolean);
  }

  open(index) {
    if (!this.modal || !this.modal.show) return;
    if (this.marquee) this.marquee.pause();
    this.modal.show(this.reviews, index, () => {
      if (this.marquee && !this.marquee.hasAttribute("data-user-paused")) this.marquee.resume();
    });
  }
}

customElements.define("testimonial-marquee", TestimonialMarquee);

class ReviewModal extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("[data-review-video]");
    this.fields = {
      author: this.querySelector("[data-review-author]"),
      handle: this.querySelector("[data-review-handle]"),
      stars: this.querySelector("[data-review-stars]"),
      title: this.querySelector("[data-review-title]"),
      text: this.querySelector("[data-review-text]"),
      textBody: this.querySelector("[data-review-text-body]"),
      more: this.querySelector("[data-review-more]"),
      product: this.querySelector("[data-review-product]"),
      productImage: this.querySelector("[data-review-product-image]"),
      productTitle: this.querySelector("[data-review-product-title]"),
      productPrice: this.querySelector("[data-review-product-price]"),
      productLinks: Array.from(this.querySelectorAll("[data-review-product-link]")),
      add: this.querySelector("[data-review-add]"),
      view: this.querySelector("[data-review-view]"),
      prev: this.querySelector("[data-review-prev]"),
      next: this.querySelector("[data-review-next]"),
      mute: this.querySelector("[data-review-mute]"),
      toggle: this.querySelector("[data-review-toggle]"),
    };
    this.reviews = [];
    this.index = 0;
    this.onClose = null;
    this._onKeydown = this._onKeydown.bind(this);

    this.querySelectorAll("[data-review-close]").forEach((el) => el.addEventListener("click", () => this.hide()));
    this.fields.prev.addEventListener("click", () => this.go(this.index - 1));
    this.fields.next.addEventListener("click", () => this.go(this.index + 1));
    this.fields.mute.addEventListener("click", () => this.setMuted(!this.video.muted));
    this.fields.toggle.addEventListener("click", () => (this.video.paused ? this.video.play() : this.video.pause()));
    this.video.addEventListener("click", () => (this.video.paused ? this.video.play() : this.video.pause()));
    this.video.addEventListener("play", () => this.classList.remove("is-paused"));
    this.video.addEventListener("pause", () => this.classList.add("is-paused"));
    this.video.addEventListener("ended", () => this.go(this.index + 1));
    this.fields.more.addEventListener("click", () => this.renderText(!this.expanded));
    this.fields.add.addEventListener("click", () => this.addToCart());
  }

  show(reviews, index, onClose) {
    this.reviews = reviews;
    this.onClose = onClose;
    this.returnFocus = document.activeElement;
    this.classList.add("is-open");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this._onKeydown);
    this.setMuted(false);
    this.go(index, true);
    this.querySelector("[data-review-close].review-modal__control")?.focus();
  }

  hide() {
    this.classList.remove("is-open");
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this._onKeydown);
    this.video.pause();
    this.video.removeAttribute("src");
    this.video.load();
    if (this.onClose) this.onClose();
    if (this.returnFocus && this.returnFocus.focus) this.returnFocus.focus();
  }

  go(index, initial) {
    if (!this.reviews.length) return;
    this.index = (index + this.reviews.length) % this.reviews.length;
    const r = this.reviews[this.index];
    const f = this.fields;

    this.video.pause();
    this.video.poster = r.poster || "";
    this.video.src = r.videoSrc;
    const playing = this.video.play();
    if (playing && playing.catch) playing.catch(() => {});

    f.author.textContent = r.author || "";
    f.handle.textContent = r.handle ? `@${r.handle.replace(/^@/, "")}` : "";
    const stars = Math.max(0, Math.min(5, Number(r.stars) || 5));
    f.stars.innerHTML = Array.from({ length: stars }, () =>
      '<svg viewBox="0 0 16 15" class="w-3 h-3" fill="currentColor" aria-hidden="true"><path d="M16 5.64L11.99 9.45l.94 5.36L8 12.28l-4.93 2.53.94-5.36L0 5.64l5.52-.78L8 0l2.48 4.86z"/></svg>'
    ).join("");

    f.title.textContent = r.title || "";
    f.title.classList.toggle("hidden", !r.title);
    f.text.classList.toggle("hidden", !r.text);
    this.renderText(false);

    const hasProduct = !!r.productTitle;
    f.product.classList.toggle("is-visible", hasProduct);
    if (hasProduct) {
      f.productImage.src = r.productImage || "";
      f.productTitle.textContent = r.productTitle;
      f.productPrice.textContent = r.productPrice || "";
      f.productLinks.forEach((link) => link.setAttribute("href", r.productUrl || "#"));
      const canAdd = r.available === "true" && !!r.variantId;
      f.add.style.display = canAdd ? "" : "none";
      f.view.style.display = canAdd ? "none" : "";
      f.view.textContent = "Sold out";
    }

    this.fields.prev.disabled = this.reviews.length < 2;
    this.fields.next.disabled = this.reviews.length < 2;
  }

  renderText(expanded) {
    const r = this.reviews[this.index];
    const text = (r && r.text) || "";
    const limit = 120;
    const needsToggle = text.length > limit + 20;
    this.expanded = expanded;
    if (!needsToggle || expanded) {
      this.fields.textBody.textContent = text;
    } else {
      const cut = text.slice(0, limit);
      this.fields.textBody.textContent = cut.slice(0, cut.lastIndexOf(" ")).trim() + "\u2026";
    }
    this.fields.more.classList.toggle("hidden", !needsToggle);
    this.fields.more.textContent = expanded ? "See less" : "See more";
  }

  setMuted(muted) {
    this.video.muted = muted;
    this.classList.toggle("is-muted", muted);
    this.fields.mute.setAttribute("aria-pressed", muted ? "true" : "false");
    this.fields.mute.setAttribute("aria-label", muted ? "Unmute" : "Mute");
  }

  async addToCart() {
    const r = this.reviews[this.index];
    const cart = document.querySelector("site-cart");
    if (!r || !r.variantId || !cart) return;
    this.fields.add.disabled = true;
    this.fields.add.textContent = "Adding";
    try {
      await cart.addVariantToCart(r.variantId, 1, null);
      this.hide();
    } finally {
      this.fields.add.disabled = false;
      this.fields.add.textContent = "Add to bag";
    }
  }

  _onKeydown(e) {
    if (e.key === "Escape") this.hide();
    if (e.key === "ArrowRight") this.go(this.index + 1);
    if (e.key === "ArrowLeft") this.go(this.index - 1);
  }
}

customElements.define("review-modal", ReviewModal);

document.addEventListener("click", (e) => {
  const link = e.target.closest("[data-back-to-top]");
  if (!link) return;
  e.preventDefault();
  window.scrollTo({ top: 0, behavior: "smooth" });
});

const nudgeAutoplayVideos = () => {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const videos = document.querySelectorAll("smart-marquee video[autoplay]");
  if (!videos.length || typeof IntersectionObserver === "undefined") return;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting) {
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      } else {
        video.pause();
      }
    });
  }, { rootMargin: "200px 0px" });
  videos.forEach((video) => observer.observe(video));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", nudgeAutoplayVideos);
} else {
  nudgeAutoplayVideos();
}

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll("[data-announcement-item]"));
    this.dots = Array.from(this.querySelectorAll("[data-announcement-dot]"));
    this.interval = parseInt(this.dataset.interval, 10) || 5000;
    this.index = 0;
    if (!this.items.length) return;

    this.items.forEach((item) => item.setAttribute("aria-hidden", "true"));
    this.dots.forEach((dot, i) => dot.addEventListener("click", () => this.go(i, true)));
    this.addEventListener("mouseenter", () => this.stop());
    this.addEventListener("mouseleave", () => this.start());
    this.go(0);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  go(index, manual) {
    this.index = (index + this.items.length) % this.items.length;
    this.items.forEach((item, i) => {
      const active = i === this.index;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-hidden", active ? "false" : "true");
    });
    this.dots.forEach((dot, i) => dot.setAttribute("aria-selected", i === this.index ? "true" : "false"));
    if (manual) {
      this.stop();
      this.start();
    }
  }

  start() {
    if (this.items.length < 2 || this.timer) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

customElements.define("announcement-bar", AnnouncementBar);

class FlavorStack extends HTMLElement {
  connectedCallback() {
    this.section = this.closest("section");
    this.bands = Array.from(this.querySelectorAll("[data-band]"));
    this.bandsWrap = this.querySelector("[data-bands]");
    if (!this.bands.length || !this.bandsWrap) return;

    this.active = 0;
    this.paused = false;
    this.visible = false;
    this.hoverable = window.matchMedia("(hover: hover)").matches;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.interval = (parseFloat(this.section.dataset.autoplay) || 0) * 1000;

    this.bands.forEach((band, i) => {
      band.addEventListener("mouseenter", () => this.hoverable && this.setActive(i));
      band.addEventListener("focusin", () => this.setActive(i));
      band.addEventListener("click", (e) => {
        if (this.hoverable && window.innerWidth >= 1024) return;
        e.preventDefault();
        this.setActive(i);
        this.restart();
      });
    });
    this.bandsWrap.addEventListener("mouseenter", () => this.hoverable && this.pause());
    this.bandsWrap.addEventListener("mouseleave", () => this.hoverable && this.resume());
    this.bandsWrap.addEventListener("focusin", () => this.pause());
    this.bandsWrap.addEventListener("focusout", () => this.resume());

    if ("IntersectionObserver" in window) {
      this.viewObserver = new IntersectionObserver((entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
      }, { threshold: 0.25 });
      this.viewObserver.observe(this.bandsWrap);
    } else {
      this.visible = true;
    }

    this.layout();
    this.restart();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    if (this.viewObserver) this.viewObserver.disconnect();
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
    this.restart();
  }

  restart() {
    clearInterval(this.timer);
    if (!this.interval || this.reducedMotion || this.bands.length < 2) return;
    this.timer = setInterval(() => {
      if (this.paused || !this.visible || document.hidden) return;
      this.setActive((this.active + 1) % this.bands.length);
    }, this.interval);
  }

  readVar(name, fallback) {
    const value = parseFloat(getComputedStyle(this.section).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  }

  setActive(index) {
    if (this.active === index) return;
    this.active = index;
    this.layout();
  }

  layout() {
    const band = this.readVar("--stack-band", 220);
    const strip = this.readVar("--stack-strip", 116);
    let y = 0;
    this.bands.forEach((el, i) => {
      el.style.transform = `translateY(${y}px)`;
      y += i === this.active ? band : strip;
    });
  }
}

customElements.define("flavor-stack", FlavorStack);
